"use client";

import { useMemo } from "react";
import { FgInput } from "@/components/fg-input";
import { AiAssistButton } from "../AiAssistButton";
import { MediaUploader } from "../MediaUploader";
import { FormValidityBanner } from "../FormValidityBanner";
import { MobilePreview } from "../MobilePreview";

export interface DictationBuilderProps {
  prompt: string;
  onPromptChange: (next: string) => void;
  audioUrl: string;
  onAudioUrlChange: (next: string) => void;
  answer: string;
  onAnswerChange: (next: string) => void;
  /** How many times the learner can replay before it's a wrong answer. */
  listenCount: number;
  onListenCountChange: (next: number) => void;
  explanation?: string;
  onExplanationChange?: (next: string) => void;
  level: "A1" | "A2" | "B1" | "B2" | "C1";
}

export function DictationBuilder({
  prompt,
  onPromptChange,
  audioUrl,
  onAudioUrlChange,
  answer,
  onAnswerChange,
  listenCount,
  onListenCountChange,
  explanation,
  onExplanationChange,
  level,
}: DictationBuilderProps) {
  const validity = useMemo(
    () => [
      {
        label: "Prompt written",
        severity: prompt.trim() ? ("ok" as const) : ("error" as const),
      },
      {
        label: "Audio uploaded or URL provided",
        severity: audioUrl ? ("ok" as const) : ("error" as const),
      },
      {
        label: "Canonical answer written",
        severity: answer.trim() ? ("ok" as const) : ("error" as const),
      },
    ],
    [prompt, audioUrl, answer],
  );

  return (
    <div className="flex flex-col gap-4">
      <FormValidityBanner title="Dictation" checks={validity} />

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.6fr,1fr]">
        <div className="flex flex-col gap-3">
          <FgInput
            label="Prompt (shown above the player)"
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            placeholder="Type what you hear."
          />
          <MediaUploader
            kind="audio"
            value={audioUrl}
            onChange={onAudioUrlChange}
            label="Audio"
            hint="Use a clear recording ≤ 10 seconds. TTS works too."
          />
          <FgInput
            label="Canonical answer"
            value={answer}
            onChange={(e) => onAnswerChange(e.target.value)}
            placeholder="Ich lerne Deutsch."
            hint="Used by Gemini as the reference — the learner isn't held to exact-match."
          />
          <FgInput
            label="Listen count (how many replays before forced wrong)"
            type="number"
            value={String(listenCount)}
            onChange={(e) => onListenCountChange(Number(e.target.value) || 1)}
            min={1}
            max={10}
          />
        </div>
        <MobilePreview
          kind="dictation"
          prompt={prompt}
          items={audioUrl ? [audioUrl, answer] : [answer]}
        />
      </div>

      <div className="flex items-center gap-3">
        <AiAssistButton
          kind="transcription"
          prompt={prompt || "Transkribiere das Audio."}
          level={level}
          label="✨ Transcribe audio"
          onApply={(content) => {
            if (typeof content === "string" && content.trim()) {
              onAnswerChange(content.trim());
            }
          }}
          disabled={!audioUrl}
        />
        <span className="text-[10px] font-mono text-[#8E8E93]">
          Uses Gemini multimodal — needs the audio URL.
        </span>
      </div>

      {onExplanationChange && (
        <FgInput
          label="Explanation (optional)"
          value={explanation ?? ""}
          onChange={(e) => onExplanationChange(e.target.value)}
        />
      )}
    </div>
  );
}